<script lang="ts">
// Re-export so existing imports (and tests) can keep pulling getMobileScreen from
// this view; the mapping itself lives in mobile-screen.ts to avoid a circular
// import with the detail tab bar.
export { getMobileScreen, type MobileScreen } from '@/components/Mobile/mobile-screen';
</script>

<script setup lang="ts">
import DoNotContactBanner from '@/components/Conversations/DoNotContact/DoNotContactBanner.vue';
import DoNotContactModal from '@/components/Conversations/DoNotContact/DoNotContactModal.vue';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';
import DoNotContactService from '@/core/conversations/do-not-contact/do-not-contact.service';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import MeService from '@/core/shared/me/me.service';
import { computed, nextTick, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getMobileScreen, getPendingConversationId } from '@/components/Mobile/mobile-screen';
import { ActivityFeedService } from '@/core/activity-feed/activity-feed.service';
import MobileTopBar from '@/components/Mobile/Shell/MobileTopBar.vue';
import MobileCandidateHeader from '@/components/Mobile/Candidate/MobileCandidateHeader.vue';
import MobileDetailTabs from '@/components/Mobile/Candidate/MobileDetailTabs.vue';
import MobileConversation from '@/components/Mobile/Conversation/MobileConversation.vue';
import MobileMessageComposer from '@/components/Mobile/Conversation/MobileMessageComposer.vue';
import MobileResume from '@/components/Mobile/Candidate/MobileResume.vue';
import MobileContact from '@/components/Mobile/Candidate/MobileContact.vue';
import MobileActivity from '@/components/Mobile/Activity/MobileActivity.vue';

const conversationIndexService = new ConversationIndexService();
const doNotContactService = new DoNotContactService();
const proProfileService = new ProProfileService();
const meService = new MeService();
const doNotContact = computed(() => conversationIndexService.selectedProDoNotContact);
const route = useRoute();
const router = useRouter();
const activityFeedService = new ActivityFeedService();

const screen = computed(() => getMobileScreen(route));

// Set while the shared jobs router is still resolving a `?conversationId=` deep link
// (an Activity tap or an email link) to its canonical candidates path. The detail
// screen renders immediately in a skeleton state instead of blocking on the fetches.
const pendingConversationId = computed(() => getPendingConversationId(route));

// While pending, the conversation services still hold the previous conversation (or
// nothing), so the header paints from the tapped row's data when we have it.
const pendingName = computed(() => {
  const id = Number(pendingConversationId.value);
  if (!id) {
    return '';
  }
  const row = activityFeedService.unreadMessages.find((message) => message.conversationId === id);
  return row?.proName ?? '';
});

// Remount the thread per conversation so each gets a fresh useConversationThread
// instance. Together with the in-flight guard in the composable this keeps a
// previous conversation's in-flight load from bleeding into the current one.
const conversationId = computed(() => {
  const id = route.params.conversationId;
  return Array.isArray(id) ? id[0] : id;
});

// The route changes before the shared selection finishes loading. Never let the
// previous candidate's banner or confirmation act on that intermediate state.
const selectionKey = computed(() => {
  const selected = conversationIndexService.selectedConversationIndex;
  const employerId = meService.employer?.id;
  if (
    screen.value === 'activity' ||
    pendingConversationId.value ||
    !employerId ||
    !selected?.proId ||
    selected.conversationId !== Number(conversationId.value) ||
    proProfileService.selectedProProfile?.id !== selected.proId
  )
    return null;
  return `${employerId}/${selected.proId}/${selected.conversationId}`;
});
const canUndo = computed(
  () => !!selectionKey.value && doNotContactService.isSelectedProDoNotContact,
);
const removalOpen = ref(false);
const isRemoving = ref(false);
const candidateDetails = ref<HTMLElement>();
let removalTarget: string | null = null;
let undoTrigger: HTMLElement | null = null;

function openRemoval(): void {
  if (!canUndo.value || isRemoving.value) return;
  removalTarget = selectionKey.value;
  const activeElement = document.activeElement;
  undoTrigger =
    activeElement instanceof HTMLElement && activeElement !== document.body ? activeElement : null;
  removalOpen.value = true;
}

async function closeRemoval(restoreFocus = true): Promise<void> {
  const target = removalTarget;
  const trigger = undoTrigger;
  removalOpen.value = false;
  removalTarget = null;
  undoTrigger = null;
  await nextTick();
  if (restoreFocus && target && target === selectionKey.value && !removalOpen.value) {
    (trigger?.isConnected ? trigger : candidateDetails.value)?.focus({ preventScroll: true });
  }
}

function updateRemovalOpen(isOpen: boolean): void {
  if (!isOpen && !isRemoving.value) void closeRemoval();
}

// Synchronous invalidation also catches switching away and back in the same tick.
watch(
  selectionKey,
  () => {
    void closeRemoval(false);
  },
  { flush: 'sync' },
);

async function submitRemoval(): Promise<void> {
  if (isRemoving.value || !removalOpen.value) return;
  if (!canUndo.value || removalTarget !== selectionKey.value) {
    void closeRemoval(false);
    return;
  }
  isRemoving.value = true;
  try {
    if (await doNotContactService.unmarkSelectedPro()) await closeRemoval();
  } finally {
    isRemoving.value = false;
  }
}

// The composer renders disabled while pending, so this send is never invoked; it
// only satisfies the composer's required handler prop.
async function pendingSend(): Promise<void> {}

// Back from a conversation drops the conversationId, which getMobileScreen maps
// back to the Activity (list) screen. While pending the canonical project isn't
// known yet, so back drops the query instead, which also aborts the resolution.
function goToActivity(): void {
  const { projectId } = route.params;
  const id = Array.isArray(projectId) ? projectId[0] : projectId;
  router.push({ path: id ? `/jobs/${id}/candidates` : '/jobs' });
}
</script>

<template>
  <div class="flex h-full flex-col" data-test="mobile-conversations-view">
    <MobileTopBar />

    <div class="flex min-h-0 flex-1 flex-col">
      <MobileActivity v-if="screen === 'activity'" class="min-h-0 flex-1" />

      <!-- Conversation / Resume / Contact share the candidate header and tab bar; the
           body below switches with the selected tab. -->
      <template v-else>
        <!-- One painted block for header + tabs: as siblings, subpixel height rounding
             on some DPRs let the page's white background show through as a hairline. -->
        <div
          ref="candidateDetails"
          tabindex="-1"
          class="bg-shade-860"
          data-test="mobile-candidate-details"
        >
          <MobileCandidateHeader
            :pending="Boolean(pendingConversationId)"
            :pending-name="pendingName"
            @back="goToActivity"
          />
          <MobileDetailTabs />
        </div>

        <DoNotContactBanner
          v-if="canUndo && doNotContact?.active"
          :summary="doNotContact"
          can-undo
          @undo="openRemoval"
        />

        <!-- Deep link still resolving: skeleton thread plus a disabled composer so the
             screen is in place the moment the real data lands. -->
        <div
          v-if="pendingConversationId"
          class="flex min-h-0 flex-1 flex-col"
          data-test="mobile-pending-screen"
        >
          <!-- Bubble shapes for the thread; neutral document lines when the deep link
               targets the resume or contact tab (email links can carry those). -->
          <div
            v-if="screen === 'conversation'"
            class="flex min-h-0 flex-1 flex-col justify-end gap-3 overflow-hidden px-4 py-3"
            data-test="mobile-pending-thread-skeleton"
          >
            <div class="h-12 w-3/5 animate-pulse self-start rounded-2xl bg-tint-80" />
            <div class="h-16 w-2/3 animate-pulse self-end rounded-2xl bg-tint-80" />
            <div class="h-10 w-1/2 animate-pulse self-start rounded-2xl bg-tint-80" />
            <div class="h-12 w-3/5 animate-pulse self-end rounded-2xl bg-tint-80" />
          </div>
          <div
            v-else
            class="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-4 py-4"
            data-test="mobile-pending-content-skeleton"
          >
            <div class="h-24 w-full animate-pulse rounded-lg bg-tint-80" />
            <div class="h-4 w-3/4 animate-pulse rounded bg-tint-80" />
            <div class="h-4 w-full animate-pulse rounded bg-tint-80" />
            <div class="h-4 w-2/3 animate-pulse rounded bg-tint-80" />
          </div>
          <MobileMessageComposer
            v-if="screen === 'conversation'"
            disabled
            :pro-name="pendingName"
            :on-send="pendingSend"
          />
        </div>

        <template v-else>
          <div
            v-if="screen === 'conversation'"
            class="flex min-h-0 flex-1 flex-col"
            data-test="mobile-conversation-screen"
          >
            <MobileConversation :key="conversationId" class="min-h-0 flex-1" />
          </div>

          <MobileResume v-else-if="screen === 'resume'" class="h-0 grow" />

          <MobileContact v-else class="h-0 grow" />
        </template>
      </template>
    </div>
    <DoNotContactModal
      :model-value="removalOpen"
      variant="remove"
      :candidate-name="conversationIndexService.selectedProName"
      :is-loading="isRemoving"
      @update:model-value="updateRemovalOpen"
      @submit="submitRemoval"
    />
  </div>
</template>
